
import { Link } from 'react-router-dom';

export default function HOAHero() {
  return (
    <section className="relative min-h-screen bg-gradient-to-br from-[#F0FDF9] via-white to-[#FFF7ED] overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-40">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#1FA372]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-[#F97316]/20 rounded-full blur-3xl"></div>
      </div>

      {/* Navigation */}
      <nav className="relative z-10 max-w-7xl mx-auto px-6 py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#1FA372] transition-colors cursor-pointer">
          <i className="ri-arrow-left-line text-xl"></i>
          <span className="font-medium">Back to Portfolio</span>
        </Link>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-12 pb-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1FA372]/10 border border-[#1FA372]/20 rounded-full mb-8">
              <span className="w-2 h-2 bg-[#1FA372] rounded-full animate-pulse"></span>
              <span className="text-[#1FA372] font-semibold text-sm">UX Case Study</span>
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold text-[#1E293B] mb-6 leading-tight">
              HOA<br />
              <span className="text-[#1FA372]">Management</span><br />
              Platform
            </h1>

            <p className="text-xl text-[#64748B] mb-10 leading-relaxed max-w-xl">
              Reimagining community management for modern homeowners associations. A comprehensive SaaS platform designed to streamline operations, enhance communication, and build stronger communities.
            </p>

            {/* Project Meta */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-5 border border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow">
                <span className="text-[#94A3B8] text-sm block mb-1">Role</span>
                <span className="text-[#1E293B] font-semibold">Lead Product Designer</span>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow">
                <span className="text-[#94A3B8] text-sm block mb-1">Duration</span>
                <span className="text-[#1E293B] font-semibold">8 Months</span>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow">
                <span className="text-[#94A3B8] text-sm block mb-1">Platform</span>
                <span className="text-[#1E293B] font-semibold">Web & Mobile</span>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow">
                <span className="text-[#94A3B8] text-sm block mb-1">Industry</span>
                <span className="text-[#1E293B] font-semibold">PropTech / Real Estate</span>
              </div>
            </div>
          </div>

          {/* Right - Dashboard Preview */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-[#1FA372]/20 to-[#F97316]/20 rounded-3xl blur-2xl"></div>
            <div className="relative bg-white rounded-3xl p-2 border border-[#E2E8F0] shadow-2xl">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[#F1F5F9]">
                <div className="w-3 h-3 rounded-full bg-[#EF4444]"></div>
                <div className="w-3 h-3 rounded-full bg-[#F97316]"></div>
                <div className="w-3 h-3 rounded-full bg-[#1FA372]"></div>
              </div>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20HOA%20homeowners%20association%20management%20dashboard%20interface%20with%20clean%20white%20light%20theme%20showing%20community%20overview%20statistics%20resident%20management%20panels%20maintenance%20requests%20and%20financial%20summaries%20clean%20enterprise%20SaaS%20design%20with%20green%20accent%20colors%20professional%20property%20management%20software&width=800&height=600&seq=hoa-dashboard-hero-light-001&orientation=landscape"
                  alt="HOA Dashboard Preview"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Floating Stats */}
            <div className="absolute -bottom-6 -left-6 bg-[#1FA372] rounded-2xl p-4 shadow-xl">
              <div className="text-white text-2xl font-bold">2.5M+</div>
              <div className="text-white/80 text-sm">Residents Served</div>
            </div>
            <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl border border-[#E2E8F0]">
              <div className="text-[#1E293B] text-2xl font-bold">98%</div>
              <div className="text-[#64748B] text-sm">User Satisfaction</div>
            </div>
          </div>
        </div>

        {/* Value Proposition Cards */}
        <div className="grid md:grid-cols-3 gap-6 mt-20">
          <div className="bg-white rounded-2xl p-6 border border-[#E2E8F0] shadow-sm hover:shadow-lg transition-all hover:-translate-y-1">
            <div className="w-14 h-14 flex items-center justify-center bg-[#1FA372]/10 rounded-xl mb-4">
              <i className="ri-community-line text-[#1FA372] text-2xl"></i>
            </div>
            <h3 className="text-[#1E293B] font-bold text-lg mb-2">Community First</h3>
            <p className="text-[#64748B] text-sm leading-relaxed">Designed to foster stronger connections between residents, boards, and property managers.</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-[#E2E8F0] shadow-sm hover:shadow-lg transition-all hover:-translate-y-1">
            <div className="w-14 h-14 flex items-center justify-center bg-[#F97316]/10 rounded-xl mb-4">
              <i className="ri-dashboard-3-line text-[#F97316] text-2xl"></i>
            </div>
            <h3 className="text-[#1E293B] font-bold text-lg mb-2">Unified Platform</h3>
            <p className="text-[#64748B] text-sm leading-relaxed">One platform for payments, maintenance, communications, and community governance.</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-[#E2E8F0] shadow-sm hover:shadow-lg transition-all hover:-translate-y-1">
            <div className="w-14 h-14 flex items-center justify-center bg-[#3B82F6]/10 rounded-xl mb-4">
              <i className="ri-global-line text-[#3B82F6] text-2xl"></i>
            </div>
            <h3 className="text-[#1E293B] font-bold text-lg mb-2">Global Scale</h3>
            <p className="text-[#64748B] text-sm leading-relaxed">Built to serve diverse markets with localized experiences for USA and India.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
